import { env } from "./env"
import { AppError } from "./problem"
import { IDEMPOTENT_OPERATIONS } from "./generated/spec-meta"
import { tokenStore } from "@/auth/token-store"
import { refreshOnce } from "@/auth/refresh"
import { onProblem } from "./on-problem"
import { currentLanguage } from "@/i18n/current-language"

/**
 * The ONE fetch in the application for API calls (LLD §1.1, §5.2; enforced by
 * lint). Every generated client call routes through here.
 *
 * Signature is orval's `httpClient: "fetch"` contract: a relative URL built by
 * the generated helper, plus a RequestInit. It returns the parsed body, and
 * THROWS an AppError on any non-2xx -- so error handling lives in one place
 * rather than branching at every call site.
 *
 * Headers: the bearer token, Accept-Language from i18n, Content-Type for a
 * body. Nothing else is invented here. `Idempotency-Key` (from the intent,
 * §5.5) and `X-Step-Up` (§6.7) are passed by the slice `api/` wrapper, and
 * every header sent must be on the backend's CORS allow-list (httpx/cors.go)
 * or a cross-origin dashboard fails every preflight (DW-42).
 */

export async function apiFetch<T>(url: string, options?: RequestInit): Promise<T> {
  return send<T>(url, options ?? {}, false)
}

async function send<T>(url: string, options: RequestInit, isRetry: boolean): Promise<T> {
  const method = (options.method ?? "GET").toUpperCase()
  const headers = new Headers(options.headers)

  headers.set("Accept", "application/json, application/problem+json")
  headers.set("Accept-Language", currentLanguage())

  const token = tokenStore.get()
  if (token) headers.set("Authorization", `Bearer ${token}`)

  if (options.body !== undefined && options.body !== null && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json")
  }

  if (import.meta.env.DEV) assertIdempotencyKey(method, url, headers)

  let res: Response
  try {
    res = await fetch(buildUrl(url), {
      ...options,
      method,
      headers,
      // The HttpOnly refresh cookie.
      credentials: "include",
    })
  } catch (cause) {
    // A thrown fetch is a transport failure, not a server answer. "You are
    // offline" and "we are broken" deserve different reactions (PDD §9.4).
    throw AppError.transport(options.signal?.aborted === true ? "timeout" : "network", cause)
  }

  if (!res.ok) {
    const err = await AppError.fromResponse(res)
    // `isRetry` is a parameter rather than a counter on purpose: a refresh
    // that yields another 401 must sign the user out, not loop.
    if (!isRetry && shouldRefresh(err, url)) {
      // A late 401: sent with token A while another request refreshed to B.
      // Replay with B rather than refreshing again.
      const current = tokenStore.get()
      if (current !== null && current !== token) return send<T>(url, options, true)
      if (await refreshOnce()) return send<T>(url, options, true)
    }
    if (!isCredentialEndpoint(url)) onProblem(err, isRetry)
    throw err
  }

  if (res.status === 204) return undefined as T

  // Content-Type decides, not a per-operation special case, so this stays
  // correct for whatever else the contract adds returning a non-JSON body.
  const contentType = res.headers.get("Content-Type") ?? ""

  // A BINARY body must never reach res.text(): it decodes the bytes as UTF-8,
  // and the PDF built from that string will not open.
  if (isBinary(contentType)) return (await res.blob()) as T

  // A 202 may carry a body or none.
  const text = await res.text()
  if (!text) return undefined as T

  // CSV exports (`text/csv`) are text, not JSON.
  if (!contentType.includes("json")) return text as T

  return JSON.parse(text) as T
}

/*
 * LLD §5.2's refresh rule (DW-64): only 401 `authentication_required` means
 * the access token is gone; every other 401 (invalid_credentials,
 * totp_invalid, session_invalid, google_token_invalid...) is the endpoint's
 * answer. The credential-taking endpoints never refresh and never trigger the
 * global reactions: their refusals belong to their screen. Matched exactly, so
 * /auth/verify-email does not swallow its /resend.
 */
const CREDENTIAL_ENDPOINTS = [
  "/auth/login",
  "/auth/google",
  "/auth/totp/verify",
  "/auth/refresh",
  "/auth/register",
  "/auth/forgot-password",
  "/auth/reset-password",
  "/auth/verify-email",
  "/auth/accept-invite",
]
const isCredentialEndpoint = (url: string): boolean =>
  CREDENTIAL_ENDPOINTS.includes(url.split("?")[0] ?? url)

function shouldRefresh(err: AppError, url: string): boolean {
  return err.status === 401 && err.code === "authentication_required" && !isCredentialEndpoint(url)
}

/**
 * Development assertion (LLD §5.2): an operation the spec marks
 * `x-idempotency: required` must arrive with its key. The server would answer
 * 422 `idempotency_key_required`; failing here names the call site instead.
 */
const IDEMPOTENT = IDEMPOTENT_OPERATIONS.map((op) => ({
  method: op.method,
  operationId: op.operationId,
  pattern: new RegExp(`^${op.path.replace(/\{[^}]+\}/g, "[^/?]+")}(\\?|$)`),
}))

function assertIdempotencyKey(method: string, url: string, headers: Headers): void {
  if (headers.has("Idempotency-Key")) return
  const op = IDEMPOTENT.find((o) => o.method === method && o.pattern.test(url))
  if (op) {
    throw new Error(
      `${op.operationId} requires an Idempotency-Key header: pass intentKey(...) from the slice api/ wrapper (LLD §5.5).`,
    )
  }
}

/**
 * Whether a response body is bytes rather than characters. Phrased as "not one
 * of the textual families", so a new binary body in the contract is handled
 * without anybody remembering to add it here.
 */
function isBinary(contentType: string): boolean {
  if (!contentType) return false
  const type = contentType.split(";")[0]?.trim().toLowerCase() ?? ""
  if (!type) return false
  if (type.startsWith("text/")) return false
  return !type.includes("json") && !type.includes("xml")
}

/**
 * Generated helpers produce a relative path with the query string already
 * appended. Empty-valued params are stripped here because an emptied filter
 * must DISAPPEAR from the request: `status=` asks the server to filter on the
 * empty string, which is a different question from "no filter".
 *
 * A same-origin base (`/api/v2`) resolves against the page origin.
 */
export function buildUrl(path: string, base: string = env.VITE_API_BASE_URL): URL {
  const url = new URL(base + path, window.location.origin)
  for (const key of [...url.searchParams.keys()]) {
    const values = url.searchParams
      .getAll(key)
      .filter((v) => v !== "" && v !== "undefined" && v !== "null")
    url.searchParams.delete(key)
    for (const v of values) url.searchParams.append(key, v)
  }
  return url
}
